import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useTranslation } from 'react-i18next'
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { Eye, EyeOff, User, Lock, Check, Loader2, Mail, Phone, ArrowRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

export default function RegisterPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [steps, setSteps] = useState([
    { id: 1, label: 'Basic Info', completed: false },
    { id: 2, label: 'Security', completed: false },
    { id: 3, label: 'Complete', completed: false }
  ])
  
  const { register } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Auto-focus on username input
  useEffect(() => {
    document.getElementById('username')?.focus()
  }, [])

  // Check password strength
  useEffect(() => {
    let strength = 0
    if (password.length >= 8) strength += 1
    if (/[A-Z]/.test(password)) strength += 1
    if (/[0-9]/.test(password)) strength += 1
    if (/[^A-Za-z0-9]/.test(password)) strength += 1
    setPasswordStrength(strength)
  }, [password])

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0: return 'bg-gray-200'
      case 1: return 'bg-red-500'
      case 2: return 'bg-yellow-500'
      case 3: return 'bg-blue-500'
      case 4: return 'bg-green-500'
      default: return 'bg-gray-200'
    }
  }

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0: return 'Very Weak'
      case 1: return 'Weak'
      case 2: return 'Fair'
      case 3: return 'Good'
      case 4: return 'Strong'
      default: return ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate inputs
    if (username.trim().length < 3) {
      return setError('Username must be at least 3 characters')
    }
    
    if (password !== confirmPassword) {
      return setError(t('register.error.match'))
    }

    if (passwordStrength < 2) {
      return setError('Please use a stronger password')
    }

    try {
      setError('')
      setLoading(true)
      
      // Simulate loading with progress
      setTimeout(() => {
        setSteps(steps.map(s => s.id === 1 ? {...s, completed: true} : s))
      }, 300)
      
      setTimeout(() => {
        setSteps(steps.map(s => s.id === 2 ? {...s, completed: true} : s))
      }, 600)
      
      await register(username, password, confirmPassword, () => {
        setSteps(steps.map(s => s.id === 3 ? {...s, completed: true} : s))
        setTimeout(() => navigate('/'), 500)
      })
    } catch (err: any) {
      setError(err.message || t('register.error.failed'))
      setSteps(steps.map(s => ({...s, completed: false})))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleRegister = async () => {
    setGoogleLoading(true)
    setError('')

    try {
      const auth = getAuth()
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({
        prompt: 'select_account'
      })
      
      await signInWithPopup(auth, provider)
      // Redirect or handle Google registration success
      navigate('/')
    } catch (err: any) {
      setError(err.message || 'Google registration failed')
    } finally {
      setGoogleLoading(false)
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 py-8">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex flex-col items-center flex-1">
                <div className="relative">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    step.completed 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : 'bg-white border-gray-300 text-gray-400'
                  } transition-all duration-300`}>
                    {step.completed ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="font-semibold">{step.id}</span>
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="absolute top-1/2 left-full w-full h-0.5 bg-gray-300 -translate-y-1/2 -translate-x-1">
                      <div className={`h-full transition-all duration-500 ${
                        steps[index + 1]?.completed || step.completed ? 'bg-green-500' : 'bg-gray-300'
                      }`} />
                    </div>
                  )}
                </div>
                <span className="mt-2 text-xs font-medium text-gray-600 hidden sm:block">
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-white/20">
          <div className="p-8 md:p-10">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
                <User className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {t('register.title')}
              </h2>
              <p className="text-gray-500 mt-2">
                Join our community today
              </p>
            </div>

            {/* Error Message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-red-100 flex-shrink-0 flex items-center justify-center">
                    <span className="text-red-600 text-sm font-bold">!</span>
                  </div>
                  <p className="text-red-600 text-sm">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.form 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              onSubmit={handleSubmit} 
              className="space-y-6"
            >
              {/* Username */}
              <motion.div variants={itemVariants}>
                <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('register.username.label')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={loading}
                    className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 transition-all duration-200 text-gray-900 placeholder:text-gray-400"
                    placeholder={t('register.username.placeholder')}
                  />
                </div>
                {username && (
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <Check className={`w-4 h-4 ${username.length >= 3 ? 'text-green-500' : 'text-gray-300'}`} />
                    <span className={username.length >= 3 ? 'text-green-600' : 'text-gray-500'}>
                      At least 3 characters
                    </span>
                  </div>
                )}
              </motion.div>

              {/* Password */}
              <motion.div variants={itemVariants}>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('register.password.label')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="w-full pl-11 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 transition-all duration-200 text-gray-900 placeholder:text-gray-400"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Password Strength */}
                {password && (
                  <div className="mt-3 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="font-medium">Password strength:</span>
                      <span className={`font-semibold ${
                        passwordStrength === 4 ? 'text-green-600' :
                        passwordStrength === 3 ? 'text-blue-600' :
                        passwordStrength === 2 ? 'text-yellow-600' :
                        passwordStrength === 1 ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {getPasswordStrengthText()}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${getPasswordStrengthColor()}`}
                        style={{ width: `${passwordStrength * 25}%` }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <Check className={`w-3 h-3 ${password.length >= 8 ? 'text-green-500' : 'text-gray-300'}`} />
                        <span>8+ characters</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className={`w-3 h-3 ${/[A-Z]/.test(password) ? 'text-green-500' : 'text-gray-300'}`} />
                        <span>Uppercase letter</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className={`w-3 h-3 ${/[0-9]/.test(password) ? 'text-green-500' : 'text-gray-300'}`} />
                        <span>Number</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className={`w-3 h-3 ${/[^A-Za-z0-9]/.test(password) ? 'text-green-500' : 'text-gray-300'}`} />
                        <span>Special character</span>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Confirm Password */}
              <motion.div variants={itemVariants}>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('register.confirmPassword.label')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="w-full pl-11 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 transition-all duration-200 text-gray-900 placeholder:text-gray-400"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {confirmPassword && password === confirmPassword && (
                  <div className="mt-2 flex items-center gap-2 text-green-600 text-xs">
                    <Check className="w-4 h-4" />
                    <span>Passwords match</span>
                  </div>
                )}
              </motion.div>

              {/* Submit Button */}
              <motion.button
                variants={itemVariants}
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3.5 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('register.submitting')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('register.submit')}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </motion.button>
            </motion.form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Google Register */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              onClick={handleGoogleRegister}
              disabled={googleLoading || loading}
              whileTap={{ scale: 0.98 }}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 py-3.5 rounded-xl font-medium shadow-sm hover:shadow-md hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              {googleLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-gray-600" />
                  <span className="text-gray-700">Connecting to Google...</span>
                </>
              ) : (
                <>
                  <div className="w-5 h-5">
                    <img src="https://www.svgrepo.com/show/355037/google.svg" alt="Google" className="w-full h-full" />
                  </div>
                  <span className="text-gray-700 group-hover:text-indigo-600 transition-colors">
                    Sign up with Google
                  </span>
                </>
              )}
            </motion.button>

            {/* Login Link */}
            <div className="mt-8 text-center">
              <p className="text-gray-600">
                {t('register.already')}{" "}
                <Link 
                  to="/login" 
                  className="text-indigo-600 font-semibold hover:text-indigo-700 hover:underline transition-colors"
                >
                  {t('register.login')}
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        <AnimatePresence>
          {(loading || googleLoading) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center"
            >
              <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-indigo-100 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                </div>
                <div className="text-center">
                  <p className="text-gray-800 font-semibold mb-1">
                    {loading ? 'Creating your account...' : 'Connecting to Google...'}
                  </p>
                  <p className="text-gray-500 text-sm">This will just take a moment</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}